/**
 * CustomersCard — customer-status breakdown for the OEM Home strip (Phase 2).
 *
 * Ports the reference HomeOEM "Customers" card: a defragmentation tile grid
 * (one block per status, sized by count) + a status legend. ILLUSTRATIVE /
 * MOCK-ONLY — there is no Customer entity in Gripp yet (PRD-deferred), so this
 * card has no "View all" destination and the legend does NOT filter the map
 * (our pins are Locations, not customers). Flagged "Sample data" in the header.
 * Consumes `useHomeCards(range)`.
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell } from './dashboardCardShell';
import { useHomeCards } from '../hooks/useHomeCards';
import { HomeRangeProps } from '../types';

// Green ramp, darkest → lightest (Active → New), matching the reference.
const STATUS_COLORS = {
  active: tokens.colors.semantic.successDark, // #24835D
  pending: tokens.colors.semantic.success, // #36B37E
  new: `${tokens.colors.semantic.success}73`, // light success green (~45%)
} as const;

/** Vertically stacked proportional blocks (one per non-zero status). */
const StatusTileGrid: React.FC<{
  items: { key: keyof typeof STATUS_COLORS; count: number }[];
}> = ({ items }) => {
  const blocks = items.filter((s) => s.count > 0);
  return (
    <View style={styles.tileGrid}>
      {blocks.map((s) => (
        <View
          key={s.key}
          style={{
            flexGrow: s.count,
            flexBasis: 0,
            borderRadius: 4,
            backgroundColor: STATUS_COLORS[s.key],
          }}
        />
      ))}
    </View>
  );
};

export const CustomersCard: React.FC<HomeRangeProps> = ({ range }) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useHomeCards(range);
  const customers = data?.customers;

  const title = t('home.customers.title', 'Customers');
  // No live destination — flag the card as illustrative instead of a View-all.
  const sampleTag = (
    <Text style={styles.sampleTag} testID="customers-card-sample">
      {t('home.customers.sample', 'Sample data')}
    </Text>
  );

  if (isLoading) {
    return (
      <DashboardCardShell title={title} action={sampleTag} testID="customers-card"
        loadingSlot={<Skeleton height={120} radius={4} />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (error) {
    return (
      <DashboardCardShell title={title} action={sampleTag} testID="customers-card"
        errorSlot={<ErrorState title={t('home.cards.error', 'Could not load data')} testID="customers-card-error" />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (!customers || customers.total === 0) {
    return (
      <DashboardCardShell title={title} action={sampleTag} testID="customers-card"
        emptySlot={<EmptyState title={t('home.customers.empty', 'No customers')} testID="customers-card-empty" />}
      >
        {null}
      </DashboardCardShell>
    );
  }

  const legend = [
    { key: 'active' as const, label: t('home.customers.active', 'Active'), count: customers.active },
    { key: 'pending' as const, label: t('home.customers.pending', 'Pending'), count: customers.pending },
    { key: 'new' as const, label: t('home.customers.new', 'New'), count: customers.new },
  ];

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(customers.total)}
      action={sampleTag}
      testID="customers-card"
    >
      <View style={styles.body}>
        <StatusTileGrid items={legend.map((l) => ({ key: l.key, count: l.count }))} />
        <View style={styles.legend}>
          {legend.map((l) => (
            <View key={l.key} style={styles.legendRow}>
              <View style={[styles.legendDot, { backgroundColor: STATUS_COLORS[l.key] }]} />
              <Text style={styles.legendLabel} numberOfLines={1}>{l.label}</Text>
              <Text style={styles.legendCount}>{l.count}</Text>
            </View>
          ))}
        </View>
      </View>
    </DashboardCardShell>
  );
};

CustomersCard.displayName = 'CustomersCard';

const styles = StyleSheet.create({
  body: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
  },
  sampleTag: {
    ...tokens.typography.label.small,
    color: tokens.colors.slate[400],
    fontStyle: 'italic',
  },
  tileGrid: {
    width: 120,
    height: 120,
    flexDirection: 'column',
    gap: 4,
  },
  legend: {
    flex: 1,
    gap: 6,
    minWidth: 0,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.borderRadius.full,
  },
  legendLabel: {
    ...tokens.typography.label.medium,
    color: tokens.colors.slate[400],
    flex: 1,
  },
  legendCount: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.neutral[800],
    fontVariant: ['tabular-nums'],
  },
});
